/**
 * SupportModal — the "Contact support" window. A plain mailto link/`window.open`
 * fails silently for users without a configured mail handler, so we show a real
 * dialog: the address (copyable) + an explicit button that opens the mail app.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Copy, LifeBuoy, Mail, X } from 'lucide-react';

const SUPPORT_EMAIL = 'support@vivideo.ai';

export function SupportModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('v2');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable — the address is visible to copy by hand */
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl bg-[#15151d] border border-white/10 p-6 shadow-2xl animate-slide-up text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-text-tertiary hover:text-text-primary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <span className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/25 to-accent/25 border border-primary/30 flex items-center justify-center">
          <LifeBuoy className="w-7 h-7 text-primary" />
        </span>

        <h3 className="text-lg font-bold text-text-primary mt-4">{t('support.heading')}</h3>
        <p className="text-sm text-text-secondary leading-relaxed mt-1.5">
          {t('support.description')}
        </p>

        {/* The address, visible + copyable */}
        <div className="flex items-center justify-center gap-2 mt-4 px-4 py-3 rounded-2xl bg-white/5 border border-white/10">
          <span className="text-sm font-semibold text-text-primary select-all">{SUPPORT_EMAIL}</span>
          <button
            onClick={() => void copy()}
            title={t('support.copyAddress')}
            className="w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-text-tertiary hover:text-text-primary transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-primary" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Explicit mail button — a real anchor, the most reliable mailto trigger */}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          onClick={onClose}
          className="mt-4 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-bold hover:opacity-95 hover:shadow-glow transition-all"
        >
          <Mail className="w-4 h-4" /> {t('support.emailButton', { email: SUPPORT_EMAIL })}
        </a>
      </div>
    </div>,
    document.body
  );
}
